import React, { useMemo } from "react";
import { useQuery } from "@apollo/client";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  useTheme,
} from "@mui/material";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";
import { GET_TOTALS_BY_DARKMODE } from "../../graphql/gqlUser";

type Row = { darkmode: string; totalUsers: number };
type Resp = { getTotalsByDarkmode: Row[] };

const LanguageTotalsPie: React.FC = () => {
  const theme = useTheme();
  const { data, loading, error } = useQuery<Resp>(GET_TOTALS_BY_DARKMODE, {
    fetchPolicy: "cache-and-network",
  });

  const rows = data?.getTotalsByDarkmode ?? [];
  const total = useMemo(
    () => rows.reduce((s, r) => s + r.totalUsers, 0),
    [rows]
  );

  const colors = [
    theme.palette.secondary.main,
    theme.palette.secondary.light,
    theme.palette.secondary.dark,
    theme.palette.primary.light,
    theme.palette.primary.main,
    theme.palette.primary.dark,
    theme.palette.success.main,
    theme.palette.info.light,
    theme.palette.warning.light,
    theme.palette.error.light,
  ];

  const chartData = useMemo(
    () =>
      rows
        .filter((r) => r.totalUsers > 0)
        .map((r) => ({ name: r.darkmode || "Unknown", value: r.totalUsers })),
    [rows]
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
      >
        <Typography variant="h6">Users by darkmode</Typography>
        {total > 0 && (
          <Typography variant="body2" color="text.secondary">
            {total.toLocaleString()} total users
          </Typography>
        )}
      </Stack>

      {error && <Alert severity="error">{error.message}</Alert>}

      {loading && !rows.length ? (
        <Box sx={{ display: "grid", placeItems: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : chartData.length === 0 ? (
        <Box sx={{ py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No data to display.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                innerRadius="45%"
                outerRadius="80%"
                paddingAngle={2}
                isAnimationActive
              >
                {chartData.map((_, i) => (
                  <Cell key={`cell-${i}`} fill={colors[i % colors.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(v: any, _n, p: any) => {
                  const value = Number(v) || 0;
                  const pct = total
                    ? ((value / total) * 100).toFixed(1) + "%"
                    : "0%";
                  return [
                    `${value.toLocaleString()} (${pct})`,
                    p?.payload?.name,
                  ];
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
};

export default LanguageTotalsPie;
